/**
 * User Dialog Component
 * 
 * Full-featured user management dialog for creating and editing users.
 * Includes permission selection, model assignment, and password management.
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  Paper,
  Collapse,
  ClickAwayListener,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { configApi, type User, type CreateUserRequest, type LookerModel, type LookerUserAttribute, type LookerTheme } from '../services/api';

// Standard Looker embed permissions
const LOOKER_PERMISSIONS = [
  'access_data',
  'see_user_dashboards',
  'see_lookml_dashboards',
  'see_looks',
  'explore',
  'create_table_calculations',
  'download_with_limit',
  'download_without_limit',
  'see_drill_overlay',
  'save_content',
];

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null; // null = create mode, User = edit mode
  onSubmit: (data: CreateUserRequest & { newPassword?: string; resetMfa?: boolean }) => void;
  onDelete?: (id: string) => void;
  isLoading?: boolean;
  loadingMessage?: string;
}

interface FormState {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  permissions: string[];
  models: string[];
  groupIds: string; // Comma-separated list of Looker group IDs
  folderIds: string; // Comma-separated list of Looker folder IDs
  userAttributes: Record<string, string>; // Looker user attribute values (e.g., { brand: "Acme Corp" })
  themeName: string; // Looker theme for embedded dashboards
  resetMfa: boolean; // Reset user's TOTP MFA
}

const defaultFormState: FormState = {
  username: '',
  password: '',
  firstName: '',
  lastName: '',
  permissions: ['access_data', 'see_user_dashboards'],
  models: [],
  groupIds: '',
  folderIds: '',
  userAttributes: {},
  themeName: '',
  resetMfa: false,
};

export function UserDialog({ open, onClose, user, onSubmit, onDelete, isLoading, loadingMessage }: UserDialogProps) {
  const [form, setForm] = useState<FormState>(defaultFormState);
  const [modelsDropdownOpen, setModelsDropdownOpen] = useState(false);
  const [modelsSearch, setModelsSearch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const modelsContainerRef = useRef<HTMLDivElement>(null);
  const modelsSearchRef = useRef<HTMLInputElement>(null);
  const isEditMode = !!user;

  // Handle close with focus management to prevent aria-hidden warning
  const handleClose = () => {
    // Blur any focused element before closing to prevent aria-hidden warning
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  // Fetch available models from Looker
  const { data: availableModels = [] } = useQuery<LookerModel[]>({
    queryKey: ['models'],
    queryFn: configApi.getModels,
    enabled: open,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Fetch available user attributes from Looker (non-system only)
  const { data: availableUserAttributes = [] } = useQuery<LookerUserAttribute[]>({
    queryKey: ['user-attributes'],
    queryFn: configApi.getUserAttributes,
    enabled: open,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Fetch available themes from Looker
  const { data: availableThemes = [], isLoading: themesLoading, isError: themesError } = useQuery<LookerTheme[]>({
    queryKey: ['themes'],
    queryFn: configApi.getThemes,
    enabled: open,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  // Reset form when dialog opens/closes or user changes
  useEffect(() => {
    if (open) {
      if (user) {
        // Strip "embed-" prefix from externalUserId for display
        const displayUsername = user.externalUserId?.replace(/^embed-/, '') || '';
        // Convert existing userAttributes to string values for form
        const existingAttrs: Record<string, string> = {};
        if (user.userAttributes) {
          for (const [key, value] of Object.entries(user.userAttributes)) {
            existingAttrs[key] = String(value ?? '');
          }
        }
        setForm({
          username: displayUsername,
          password: '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          permissions: user.permissions || [],
          models: user.models || [],
          groupIds: (user.groupIds || []).join(','),
          folderIds: (user.folderIds || []).join(','),
          userAttributes: existingAttrs,
          themeName: user.themeName || '',
          resetMfa: false,
        });
      } else {
        setForm(defaultFormState);
      }
      setModelsDropdownOpen(false);
      setShowPassword(false);
      setPasswordFocused(false);
    }
  }, [open, user]);

  const handlePermissionToggle = (permission: string) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const handleModelToggle = (modelName: string) => {
    setForm((prev) => ({
      ...prev,
      models: prev.models.includes(modelName)
        ? prev.models.filter((m) => m !== modelName)
        : [...prev.models, modelName],
    }));
  };

  const handleRemoveModel = (model: string) => {
    setForm((prev) => ({ ...prev, models: prev.models.filter((m) => m !== model) }));
  };

  // Filter models based on search query
  const filteredModels = availableModels.filter((model) =>
    model.name.toLowerCase().includes(modelsSearch.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert comma-separated groupIds string to array of numbers
    const groupIdsArray = form.groupIds
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '')
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));

    // Convert comma-separated folderIds string to array of strings
    const folderIdsArray = form.folderIds
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '');
    
    // Build userAttributes, omitting empty values
    const userAttributes: Record<string, string> = {};
    for (const [key, value] of Object.entries(form.userAttributes)) {
      if (value.trim()) {
        userAttributes[key] = value.trim();
      }
    }

    onSubmit({
      username: form.username,
      password: form.password,
      userType: 'viewer', // All users created via UI are viewers
      firstName: form.firstName,
      lastName: form.lastName,
      permissions: form.permissions,
      models: form.models,
      groupIds: groupIdsArray,
      externalGroupId: '',
      userAttributes,
      folderIds: folderIdsArray.length > 0 ? folderIdsArray : undefined,
      themeName: form.themeName || undefined,
      newPassword: isEditMode && form.password ? form.password : undefined,
      resetMfa: isEditMode && form.resetMfa ? true : undefined,
    });
  };

  // Show password requirements only when user has typed something
  const showPasswordRequirements = passwordFocused && form.password.length > 0;

  // Password validation checks
  const passwordChecks = {
    length: form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    lowercase: /[a-z]/.test(form.password),
    number: /[0-9]/.test(form.password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(form.password),
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      disableEnforceFocus
      disableRestoreFocus
      disableAutoFocus
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 2,
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '63%' },
          maxWidth: 630,
          boxShadow: '-20px 0 60px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {/* Loading Overlay */}
        {isLoading && loadingMessage && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.55)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: 'white' }} />
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.95rem', letterSpacing: 0.2 }}>
              {loadingMessage}
            </Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2.5,
              px: 3,
              borderBottom: 1,
              borderColor: 'divider',
              background: 'linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%)',
              color: 'white',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '1.125rem',
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              {isEditMode ? (
                <EditIcon sx={{ width: 20, height: 20 }} />
              ) : (
                <PersonAddIcon sx={{ width: 20, height: 20 }} />
              )}
              {isEditMode ? 'Edit User' : 'Create New User'}
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: '1.25rem',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body - Scrollable */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {/* Account Credentials Section */}
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
                mb: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              Account Credentials
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                label="Username (Email)"
                fullWidth
                required
                disabled={isEditMode}
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="example@company.com"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{
                  '& .Mui-disabled': {
                    bgcolor: 'grey.100',
                    cursor: 'not-allowed',
                  },
                }}
                helperText={isEditMode ? 'Username cannot be changed' : ''}
              />
              <Box>
                <TextField
                  label={isEditMode ? 'New Password' : 'Password'}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  required={!isEditMode || form.resetMfa}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  placeholder="Password"
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            tabIndex={-1}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: 'text.primary', bgcolor: 'grey.100' },
                            }}
                          >
                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Password Requirements */}
            <Collapse in={showPasswordRequirements}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  bgcolor: 'grey.50',
                  borderColor: 'grey.200',
                }}
              >
                {isEditMode && (
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    Leave blank to keep current password
                  </Typography>
                )}
                <Box component="ul" sx={{ m: 0, pl: 0, listStyleType: 'none' }}>
                  {[
                    { key: 'length', label: 'At least 8 characters', valid: passwordChecks.length },
                    { key: 'uppercase', label: 'One uppercase letter', valid: passwordChecks.uppercase },
                    { key: 'lowercase', label: 'One lowercase letter', valid: passwordChecks.lowercase },
                    { key: 'number', label: 'One number', valid: passwordChecks.number },
                    { key: 'special', label: 'One special character (!@#$%^&*)', valid: passwordChecks.special },
                  ].map((check) => (
                    <Box
                      component="li"
                      key={check.key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        my: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          flexShrink: 0,
                          bgcolor: check.valid ? '#10b981' : '#ef4444',
                          color: 'white',
                        }}
                      >
                        {check.valid ? '✓' : '✕'}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: check.valid ? '#10b981' : '#ef4444',
                          lineHeight: 1.4,
                          fontWeight: check.valid ? 500 : 400,
                        }}
                      >
                        {check.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Collapse>

            {/* MFA Reset - Edit mode only */}
            {isEditMode && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  borderColor: form.resetMfa ? '#fbbf24' : 'grey.200',
                  bgcolor: form.resetMfa ? '#fffbeb' : 'transparent',
                  transition: 'all 0.2s ease',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={form.resetMfa}
                      onChange={(e) => setForm({ ...form, resetMfa: e.target.checked })}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      Reset MFA (Authenticator App)
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Paper>
            )}

            {/* Profile Section */}
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
                mb: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              Profile
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
              <TextField
                label="First Name"
                fullWidth
                required
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                placeholder="John"
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                label="Last Name"
                fullWidth
                required
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                placeholder="Doe"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>

            {/* Looker Embed Settings Section */}
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
                mb: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              Looker Embed Settings
            </Typography>

            {/* Permissions Grid */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Permissions
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 0.5,
                bgcolor: 'background.paper',
              }}
            >
              {LOOKER_PERMISSIONS.map((permission) => (
                <FormControlLabel
                  key={permission}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.permissions.includes(permission)}
                      onChange={() => handlePermissionToggle(permission)}
                    />
                  }
                  label={<Typography variant="body2">{permission}</Typography>}
                  sx={{
                    m: 0,
                    py: 0.5,
                    px: 1,
                    borderRadius: 0.5,
                    '&:hover': { bgcolor: 'grey.50' },
                  }}
                />
              ))}
            </Paper>

            {/* Models Dropdown */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Models
            </Typography>
            <Box sx={{ position: 'relative', mb: 2 }} ref={modelsContainerRef}>
              <ClickAwayListener onClickAway={() => {
                setModelsDropdownOpen(false);
                setModelsSearch('');
              }}>
                <Box>
                  <Paper
                    variant="outlined"
                    onClick={() => {
                      setModelsDropdownOpen(true);
                      // Focus search input after dropdown opens
                      setTimeout(() => modelsSearchRef.current?.focus(), 50);
                    }}
                    sx={{
                      p: 1,
                      minHeight: 44,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s ease',
                      '&:hover': {
                        borderColor: 'grey.400',
                      },
                      ...(modelsDropdownOpen && {
                        borderColor: 'primary.main',
                        outline: '2px solid rgba(24, 24, 27, 0.05)',
                      }),
                    }}
                  >
                    {form.models.length > 0 ? (
                      form.models.map((model) => (
                        <Chip
                          key={model}
                          label={model}
                          size="small"
                          onDelete={() => handleRemoveModel(model)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            bgcolor: '#e0f2fe',
                            color: '#0284c7',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            '& .MuiChip-deleteIcon': {
                              color: '#0284c7',
                              '&:hover': { color: '#dc2626' },
                            },
                            '&:hover': {
                              bgcolor: '#fecaca',
                              color: '#dc2626',
                            },
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                        Select models
                      </Typography>
                    )}
                  </Paper>

                  {/* Dropdown list */}
                  {modelsDropdownOpen && (
                    <Paper
                      elevation={8}
                      sx={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: 250,
                        overflow: 'hidden',
                        borderRadius: 2,
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModelsDropdownOpen(false);
                          setModelsSearch('');
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 10,
                          width: 28,
                          height: 28,
                          bgcolor: 'white',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                          '&:hover': {
                            bgcolor: '#ef4444',
                            color: 'white',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>

                      {/* Search input */}
                      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'grey.200' }}>
                        <TextField
                          inputRef={modelsSearchRef}
                          size="small"
                          fullWidth
                          placeholder="Search models..."
                          value={modelsSearch}
                          onChange={(e) => setModelsSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoComplete="off"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'grey.50',
                              '& fieldset': {
                                borderColor: 'grey.300',
                              },
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        {availableModels.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 2, textAlign: 'center' }}
                          >
                            No models available or API not configured
                          </Typography>
                        ) : filteredModels.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 2, textAlign: 'center' }}
                          >
                            No models match &quot;{modelsSearch}&quot;
                          </Typography>
                        ) : (
                          filteredModels.map((model) => {
                            const isSelected = form.models.includes(model.name);
                            return (
                              <Box
                                key={model.name}
                                onClick={() => handleModelToggle(model.name)}
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                  cursor: 'pointer',
                                  borderBottom: '1px solid',
                                  borderColor: 'grey.100',
                                  fontSize: '0.875rem',
                                  bgcolor: isSelected ? '#e0f2fe' : 'transparent',
                                  color: isSelected ? '#0284c7' : 'text.primary',
                                  fontWeight: isSelected ? 500 : 400,
                                  '&:hover': {
                                    bgcolor: isSelected ? '#e0f2fe' : 'grey.100',
                                  },
                                  '&:last-child': {
                                    borderBottom: 'none',
                                  },
                                }}
                              >
                                {model.name} {isSelected && '✓'}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Paper>
                  )}
                </Box>
              </ClickAwayListener>
            </Box>

            {/* Looker Group IDs */}
            <TextField
              label="Looker Group ID(s)"
              fullWidth
              required
              value={form.groupIds}
              onChange={(e) => setForm({ ...form, groupIds: e.target.value })}
              placeholder="82"
              helperText="Enter Looker group ID(s). Multiple IDs can be comma-separated (e.g., 82,45)"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ mb: 3 }}
            />

            {/* Dashboard Folder IDs */}
            <TextField
              label="Dashboard Folder ID(s)"
              fullWidth
              value={form.folderIds}
              onChange={(e) => setForm({ ...form, folderIds: e.target.value })}
              placeholder="123"
              helperText="Looker folder IDs whose dashboards this user can view. Comma-separated (e.g., 123,456). Leave empty to use the global default folder."
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ mb: 3 }}
            />

            {/* Embed Theme */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Embed Theme
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {themesLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading themes from Looker...
                </Typography>
              ) : themesError ? (
                <Box>
                  <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                    Could not load themes from Looker. You can still enter a theme name manually.
                  </Typography>
                  <TextField
                    label="Theme Name"
                    size="small"
                    fullWidth
                    value={form.themeName}
                    onChange={(e) => setForm({ ...form, themeName: e.target.value })}
                    placeholder="e.g., embedded_viewer"
                    helperText="Enter the exact theme name from Looker Admin > Themes"
                  />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Chip
                      label="None"
                      size="small"
                      variant={!form.themeName ? 'filled' : 'outlined'}
                      onClick={() => setForm({ ...form, themeName: '' })}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: !form.themeName ? 'grey.200' : 'transparent',
                        color: !form.themeName ? 'text.primary' : 'text.secondary',
                        fontWeight: !form.themeName ? 500 : 400,
                        '&:hover': {
                          bgcolor: !form.themeName ? 'grey.200' : 'grey.100',
                        },
                      }}
                    />
                    {availableThemes.map((theme) => (
                      <Chip
                        key={theme.id}
                        label={theme.name}
                        size="small"
                        variant={form.themeName === theme.name ? 'filled' : 'outlined'}
                        onClick={() => setForm({ ...form, themeName: theme.name })}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: form.themeName === theme.name ? '#e0f2fe' : 'transparent',
                          color: form.themeName === theme.name ? '#0284c7' : 'text.secondary',
                          fontWeight: form.themeName === theme.name ? 500 : 400,
                          '&:hover': {
                            bgcolor: form.themeName === theme.name ? '#e0f2fe' : 'grey.100',
                          },
                        }}
                      />
                    ))}
                  </Box>
                  {availableThemes.length === 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        No themes found in Looker. Create themes in Looker Admin → Themes, or enter a name manually:
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={form.themeName}
                        onChange={(e) => setForm({ ...form, themeName: e.target.value })}
                        placeholder="Enter theme name manually"
                        helperText="Must match a theme name in Looker exactly"
                      />
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Themes control dashboard appearance (hide filters, colors, etc.). Select a theme or leave as &quot;None&quot; to use Looker&apos;s default.
                  </Typography>
                </>
              )}
            </Paper>

            {/* User Attributes Section */}
            <Typography
              variant="subtitle2"
              component="h3"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.875rem',
                mb: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              User Attributes
            </Typography>

            {availableUserAttributes.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: -0.5 }}>
                  Values set here are passed to Looker during embed sessions. Use these to control data filtering
                  (e.g., restrict a user to only see their business&apos;s data via access filters defined in LookML).
                </Typography>
                {availableUserAttributes.map((attr) => (
                  <TextField
                    key={attr.name}
                    label={attr.label}
                    fullWidth
                    value={form.userAttributes[attr.name] || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        userAttributes: {
                          ...prev.userAttributes,
                          [attr.name]: e.target.value,
                        },
                      }))
                    }
                    placeholder={attr.default_value || `Enter ${attr.label.toLowerCase()}`}
                    helperText={
                      <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Box
                          component="code"
                          sx={{
                            px: 0.5,
                            py: 0.25,
                            bgcolor: 'grey.100',
                            borderRadius: 0.5,
                            fontSize: '0.7rem',
                          }}
                        >
                          {attr.name}
                        </Box>
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          Type: {attr.type}
                        </Box>
                      </Box>
                    }
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                ))}
              </Box>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderColor: 'grey.200',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No custom user attributes found in Looker.
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  Define user attributes in Looker Admin to enable per-user data filtering.
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
              display: 'flex',
              justifyContent: isEditMode ? 'space-between' : 'flex-end',
              flexShrink: 0,
            }}
          >
            {isEditMode && onDelete && (
              <Button
                color="error"
                variant="outlined"
                onClick={(e) => {
                  e.currentTarget.blur();
                  setDeleteConfirmOpen(true);
                }}
                sx={{
                  borderColor: '#fecaca',
                  color: 'error.main',
                  '&:hover': {
                    borderColor: '#fca5a5',
                    bgcolor: '#fef2f2',
                  },
                }}
              >
                Delete User
              </Button>
            )}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  background: 'linear-gradient(135deg, #18181b 0%, #2d2d44 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2d2d44 0%, #3d3d5c 100%)',
                  },
                }}
              >
                {isEditMode ? 'Save Changes' : 'Create User'}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Drawer>

    <Dialog
      open={deleteConfirmOpen}
      onClose={() => setDeleteConfirmOpen(false)}
      aria-labelledby="delete-user-dialog-title"
      aria-describedby="delete-user-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minWidth: 420,
        },
      }}
    >
      <DialogTitle id="delete-user-dialog-title" sx={{ fontWeight: 600 }}>
        Delete user?
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-user-dialog-description">
          This will permanently delete{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {user?.firstName} {user?.lastName}
          </Box>
          . This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setDeleteConfirmOpen(false);
            if (user && onDelete) {
              onDelete(user.id);
            }
          }}
        >
          Delete User
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default UserDialog;
